'use client';

import { useState } from 'react';
import { toast } from '@/lib/toast';
import { UserMultiSelectDropdown } from './user-multi-select-dropdown';
import {
  assignMatterRoleAction,
  removeMatterRoleAction
} from '@/lib/services/matter-roles/actions';
import { MatterRole } from '@/lib/services/matter-roles/types';
import { Label } from '@/components/ui/label';
import type { LawFirmUser } from '@/lib/api/account-api';

interface TeamMemberSelectorProps {
  users: LawFirmUser[]; // Pass users from parent (already cached)
  usersLoading?: boolean; // Pass loading state from parent
  usersError?: string | null; // Pass error state from parent
  matterId?: number; // Optional for create workflow
  selectedUserIds: string[];
  onSelectionChange: (userIds: string[]) => void;
  excludeUserIds?: string[];
  label?: string;
  description?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  // If matterId is provided, will make real API calls for immediate assignment
  // If not provided, will just manage local state for form submission later
  immediateAssignment?: boolean;
}

/**
 * Reusable team member selector component
 * Handles both immediate API assignment (for edit forms) and deferred assignment (for create forms)
 */
export function TeamMemberSelector({
  users,
  usersLoading = false,
  usersError = null,
  matterId,
  selectedUserIds,
  onSelectionChange,
  excludeUserIds = [],
  label = 'Team Members',
  description = 'Select team members to assist with this matter',
  placeholder = 'Select team members...',
  className,
  disabled = false,
  immediateAssignment = false
}: TeamMemberSelectorProps) {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSelectionChange = async (userIds: string[]) => {
    if (immediateAssignment && matterId) {
      // Make immediate API calls for edit forms
      setIsUpdating(true);
      try {
        const currentIds = selectedUserIds;
        const newIds = userIds;

        // Determine changes
        const toAdd = newIds.filter((id) => !currentIds.includes(id));
        const toRemove = currentIds.filter((id) => !newIds.includes(id));

        // Remove users no longer selected
        for (const userId of toRemove) {
          const userIdNum = parseInt(userId, 10);
          if (isNaN(userIdNum)) continue; // Skip invalid IDs

          await removeMatterRoleAction(matterId, userIdNum);
        }

        // Add newly selected users
        for (const userId of toAdd) {
          const userIdNum = parseInt(userId, 10);
          if (isNaN(userIdNum)) continue; // Skip invalid IDs

          await assignMatterRoleAction({
            matterId,
            userId: userIdNum,
            role: MatterRole.ASSISTING_MEMBER
          });
        }

        onSelectionChange(userIds);

        if (toAdd.length > 0 || toRemove.length > 0) {
          toast.success('Team member assignments updated');
        }
      } catch (error) {
        console.error('Error updating team members:', error);
        toast.error('Failed to update team member assignments');
      } finally {
        setIsUpdating(false);
      }
    } else {
      // Just update local state for create forms
      onSelectionChange(userIds);
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <Label>{label}</Label>
      <UserMultiSelectDropdown
        users={users}
        selectedUserIds={selectedUserIds}
        onSelectionChange={handleSelectionChange}
        excludeUserIds={excludeUserIds}
        loading={usersLoading || isUpdating}
        error={usersError}
        placeholder={placeholder}
        disabled={disabled}
        maxDisplay={3}
      />
      {description && (
        <p className="text-muted-foreground text-sm">{description}</p>
      )}
      {isUpdating && (
        <p className="text-muted-foreground text-sm">
          Updating team member assignments...
        </p>
      )}
    </div>
  );
}
